"use client";

import React from "react";
import { unified } from "unified";
import rehypeParse from "rehype-parse";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import rehypeReact from "rehype-react";
import * as jsxRuntime from "react/jsx-runtime";

const schema = {
  ...defaultSchema,

  tagNames: [
    ...(defaultSchema.tagNames ?? []),
    "span",
    "div",
    "br",
    "hr",
    "img",
    "figure",
    "figcaption",
    "iframe",
  ],

  attributes: {
    ...(defaultSchema.attributes ?? {}),

    a: [
      ...(defaultSchema.attributes?.a ?? []),
      "href",
      "target",
      "rel",
      "title",
    ],

    img: [
      ...(defaultSchema.attributes?.img ?? []),
      "src",
      "alt",
      "title",
      "width",
      "height",
      "loading",
      "decoding",
    ],

    iframe: [
      "src",
      "width",
      "height",
      "title",
      "allow",
      "allowfullscreen",
      "frameborder",
    ],

    "*": [
      ...(defaultSchema.attributes?.["*"] ?? []),
      "className",
      "class",
    ],
  },

  protocols: {
    ...(defaultSchema.protocols ?? {}),
    href: ["http", "https", "mailto", "tel"],
    src: ["http", "https"],
  },
};

function applyBasicBoldMarkdown(input: string): string {
  const parts = input.split(/(<[^>]+>)/g);

  return parts
    .map((part) => {
      if (part.startsWith("<")) return part;

      return part
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/__(.+?)__/g, "<strong>$1</strong>");
    })
    .join("");
}

function normalizeHtml(html: string): string {
  let out = html;

  out = out.replace(/<strong>\s*<strong>/gi, "<strong>");
  out = out.replace(/<\/strong>\s*<\/strong>/gi, "</strong>");
  out = out.replace(/<em>\s*<em>/gi, "<em>");
  out = out.replace(/<\/em>\s*<\/em>/gi, "</em>");

  out = out.replace(
    /<(script|style|object|embed|link|meta|base)\b[^>]*>[\s\S]*?<\/\1>/gi,
    ""
  );
  out = out.replace(
    /<(script|style|object|embed|link|meta|base)\b[^>]*\/?>/gi,
    ""
  );

  out = out.replace(/\son\w+\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, "");

  return out;
}

type Props = {
  content?: string | null;
  className?: string;
  flow?: boolean;
  variant?: "default" | "product";
};

export default function SafeHtml({
  content,
  className,
  flow = false,
  variant = "default",
}: Props) {
  const raw = (content ?? "").toString();
  if (!raw) return null;

  const cleaned = normalizeHtml(applyBasicBoldMarkdown(raw));

  const node = unified()
    .use(rehypeParse, { fragment: true })
    .use(rehypeSanitize, schema)
    .use(rehypeReact, {
      jsx: jsxRuntime.jsx,
      jsxs: jsxRuntime.jsxs,
      Fragment: jsxRuntime.Fragment,
    })
    .processSync(cleaned).result as React.ReactNode;

  const flowClass = flow
    ? variant === "product"
      ? "content-flow product-flow"
      : "content-flow"
    : "";

  return (
    <div className={`${flowClass} ${className ?? ""}`.trim()}>
      {node}
    </div>
  );
}